import { WithSSFSentryTags } from '@/components'
import {
  AddCompanyPage,
  AddCompanyUserPage,
  AddInsurancesPage,
  AddLocationPage,
  AddPaymentPage,
  BuildingLead,
  CartPage,
  CheckoutPage,
  ConditionalRecommendation,
  ContentConditionalOffer,
  ContractsOverview,
  CyberConditionalOffer,
  CyberLead,
  Dashboard,
  DocumentsPage,
  EditCompanyPage,
  EditLocationPage,
  EditPaymentPage,
  ElectronicsConditionalOffer,
  FindInsurancesPage,
  ImprintPage,
  InitialInformationPage,
  InsuranceViewPage,
  IntegrationsRedirect,
  IntegrationsRedirectFailed,
  LegalConditionalOffer,
  LiabilityConditionalOffer,
  LocationPage,
  PartnerAssessmentPage,
  PoaSignPage,
  PolicyPage,
  ProfessionalLiabilityLead,
  Profile,
  PublicCheckoutPage,
  RecommendationPage,
  ReferralPage,
  RequestOffer,
  SelfAssessmentPage,
  Services,
  Terms,
} from '@/pages'
import { AddInsuranceContractsPage } from '@/pages/AddInsuranceContractsPage'
import YearlyAssessment from '@/pages/yearlyAssessment'
import { returns } from '@/utils'
import { both, evolve, filter, map, pipe, pluck, prop, tail, when } from 'ramda'
import React, { JSX } from 'react'
import { Navigate, RouteObject, useLocation } from 'react-router-dom'
import { config } from '../config'
import { AuthRedirect } from './AuthRedirect'
import { ProtectedRoute } from './ProtectedRoute'
import { SignupRoute } from './SignupRoute'
import { TempAuthGuard } from './TempAuthGuard'
import { WithFunnelGuard } from './WithFunnelGuard'
import { paths } from './paths'
import { ClaimsPage } from '@/pages/ClaimsPage/Page'
import { ClaimViewPage } from '@/pages/ClaimViewPage/Page'

type Route = RouteObject & {
  public?: boolean
  path?: string
  element?: React.ReactNode
  children?: Route[]
}

const RedirectPreservingQuery = ({ to }: { to: string }): JSX.Element => {
  const location = useLocation()
  return <Navigate to={{ pathname: to, search: location.search }} />
}

const RootEntryPoint: Record<string, JSX.Element> = {
  [config.liabilityLeadDomain]: <Navigate to={paths.liabilityLead} />,
  [config.cyberLeadDomain]: <Navigate to={paths.cyberLead} />,
  [config.financialLossLeadDomain]: <Navigate to={paths.financialLossLead} />,
  [config.professionalLiabilityLeadDomain]: (
    <Navigate to={paths.professionalLiabilityLead} />
  ),
  [config.buildingLeadDomain]: <Navigate to={paths.buildingLead} />,
  [config.legalLeadDomain]: <Navigate to={paths.legalLead} />,
  [config.contentLeadDomain]: <Navigate to={paths.contentLead} />,
  [config.liabilitySsfDomain]: <Navigate to={paths.liabilitySSF} />,
  [config.contentSsfDomain]: <Navigate to={paths.contentSSF} />,
  [config.legalSsfDomain]: <Navigate to={paths.legalSSF} />,
}

export const routes: Route[] = [
  {
    path: paths.requestOffer,
    element: <RequestOffer />,
  },
  {
    path: paths.contractsOverview,
    element: <ContractsOverview />,
  },
  {
    path: paths.documents,
    element: <DocumentsPage />,
  },
  {
    path: paths.claims,
    element: <ClaimsPage />,
  },
  {
    path: paths.viewClaim,
    element: <ClaimViewPage />,
  },
  {
    path: paths.assessment,
    element: <SelfAssessmentPage />,
  },
  {
    path: paths.partnerAssessment,
    element: <PartnerAssessmentPage />,
  },
  {
    path: paths.services,
    element: <Services />,
  },
  {
    path: paths.referral,
    element: <ReferralPage />,
  },
  {
    path: paths.addUser,
    element: <AddCompanyUserPage />,
  },
  {
    path: paths.editPayment,
    element: <EditPaymentPage />,
  },
  {
    path: paths.addPayment,
    element: <AddPaymentPage />,
  },
  {
    path: paths.profile,
    element: <Profile />,
  },
  {
    path: paths.editCompany,
    element: <EditCompanyPage />,
  },
  {
    path: paths.addCompany,
    element: <AddCompanyPage />,
  },
  {
    path: paths.poaCompany,
    element: <PoaSignPage />,
  },
  {
    path: paths.imprint,
    element: <ImprintPage />,
    public: true,
  },
  {
    path: paths.initialInformation,
    element: <InitialInformationPage />,
    public: true,
  },
  {
    path: paths.policy,
    element: <PolicyPage />,
    public: true,
  },
  {
    path: paths.terms,
    element: <Terms />,
    public: true,
  },
  {
    path: paths.addLocation,
    element: <AddLocationPage />,
  },
  {
    path: paths.viewInsurance,
    element: <InsuranceViewPage />,
  },
  {
    path: paths.editLocation,
    element: <EditLocationPage />,
  },
  {
    path: paths.viewLocation,
    element: <LocationPage />,
  },
  {
    path: paths.addInsurance,
    element: <AddInsurancesPage />,
  },
  {
    path: paths.dashboard,
    element: <Dashboard />,
  },
  {
    path: paths.cart,
    element: <CartPage />,
  },
  {
    path: paths.checkout,
    element: <CheckoutPage />,
  },
  { path: paths.signup, element: <SignupRoute /> },
  {
    path: paths.authRedirect,
    element: RootEntryPoint[window.location.host] || <AuthRedirect />,
    public: [
      config.liabilityLeadDomain,
      config.cyberLeadDomain,
      config.financialLossLeadDomain,
      config.professionalLiabilityLeadDomain,
      config.buildingLeadDomain,
      config.legalLeadDomain,
      config.contentLeadDomain,
      config.liabilitySsfDomain,
      config.contentSsfDomain,
      config.legalSsfDomain,
    ].includes(window.location.host),
  },
  { path: paths.findInsurances, element: <FindInsurancesPage /> },
  {
    path: paths.integrationsRedirect,
    element: <IntegrationsRedirect />,
  },
  {
    path: paths.integrationsRedirectFailed,
    element: <IntegrationsRedirectFailed />,
  },
  {
    path: paths.viewRecommendation,
    element: <RecommendationPage />,
  },
  {
    path: paths.yearlyAssessment,
    element: <YearlyAssessment />,
  },
  {
    path: paths.addInsuranceContract,
    element: <AddInsuranceContractsPage />,
  },
  {
    path: paths.landingPageLiabilitySSF,
    element: <RedirectPreservingQuery to={paths.liabilitySSF} />,
    public: true,
  },
  {
    path: paths.liabilitySSF,
    element: WithSSFSentryTags(<LiabilityConditionalOffer />),
    public: true,
  },
  {
    path: paths.landingPageElectronicsSSF,
    element: <Navigate to={paths.electronicsSSF} />,
    public: true,
  },
  {
    path: paths.electronicsSSF,
    element: WithSSFSentryTags(<ElectronicsConditionalOffer />),
    public: true,
  },
  {
    path: paths.liabilitySSFB,
    element: <Navigate to={paths.liabilitySSF} />,
    public: true,
  },
  {
    path: paths.landingPageContentSSF,
    element: <Navigate to={paths.contentSSF} />,
    public: true,
  },
  {
    path: paths.contentSSF,
    element: WithSSFSentryTags(<ContentConditionalOffer />),
    public: true,
  },
  {
    path: paths.landingPageLegalSSF,
    element: <Navigate to={paths.legalSSF} />,
    public: true,
  },
  {
    path: paths.legalSSF,
    element: WithSSFSentryTags(<LegalConditionalOffer />),
    public: true,
  },
  {
    path: paths.landingPageCyberSSF,
    element: <Navigate to={paths.cyberSSF} />,
    public: true,
  },
  {
    path: paths.cyberSSF,
    element: WithSSFSentryTags(<CyberConditionalOffer />),
    public: true,
  },
  {
    path: paths.liabilityLead,
    element: <Navigate to={paths.liabilitySSF} />,
    public: true,
  },
  {
    path: paths.liabilityLeadB,
    element: <Navigate to={paths.liabilitySSF} />,
    public: true,
  },
  {
    path: paths.landingPageLiabilityLead,
    element: <Navigate to={paths.liabilitySSF} />,
    public: true,
  },
  {
    path: paths.cyberLead,
    element: <CyberLead />,
    public: true,
  },
  {
    path: paths.landingPageCyberLead,
    element: <Navigate to={paths.cyberLead} />,
    public: true,
  },
  {
    path: paths.financialLossLead,
    element: <Navigate to={paths.liabilitySSF} />,
    public: true,
  },
  {
    path: paths.landingPageFinancialLossLead,
    element: <Navigate to={paths.liabilitySSF} />,
    public: true,
  },
  {
    path: paths.professionalLiabilityLead,
    element: <ProfessionalLiabilityLead />,
    public: true,
  },
  {
    path: paths.landingPageProfessionalLiabilityLead,
    element: <Navigate to={paths.professionalLiabilityLead} />,
    public: true,
  },
  {
    path: paths.buildingLead,
    element: <BuildingLead />,
    public: true,
  },
  {
    path: paths.landingPageBuildingLead,
    element: <Navigate to={paths.buildingLead} />,
    public: true,
  },
  {
    path: paths.contentLead,
    element: <Navigate to={paths.contentSSF} />,
    public: true,
  },
  {
    path: paths.landingPageContentLead,
    element: <Navigate to={paths.contentSSF} />,
    public: true,
  },
  {
    path: paths.landingPageLegalLead,
    element: <Navigate to={paths.legalSSF} />,
    public: true,
  },
  {
    path: paths.legalLead,
    element: <Navigate to={paths.legalSSF} />,
    public: true,
  },
  {
    path: '',
    public: true,
    element: <TempAuthGuard />,
    children: [
      {
        path: tail(paths.ssfCheckout),
        element: WithSSFSentryTags(<PublicCheckoutPage />),
      },
      {
        path: tail(paths.ssfOffer),
        element: WithSSFSentryTags(<ConditionalRecommendation />),
      },
    ],
  },
]

const withoutLeadingSlash = evolve({
  path: tail,
})

const isNotPublic = (route: Route): boolean => !route.public

const makeProtectedRoutes = when(
  both(
    isNotPublic,
    returns(
      ![
        config.liabilityLeadDomain,
        config.cyberLeadDomain,
        config.financialLossLeadDomain,
        config.professionalLiabilityLeadDomain,
        config.buildingLeadDomain,
        config.legalLeadDomain,
        config.contentLeadDomain,
        config.liabilitySsfDomain,
        config.contentSsfDomain,
        config.legalSsfDomain,
      ].includes(window.location.host),
    ),
  ),
  evolve({
    element: (element: React.ReactNode) => (
      <ProtectedRoute element={element as JSX.Element} />
    ),
  }),
)

export const transformRoutes = (pipe(
  // @ts-expect-error solve after removing ramda
  map(withoutLeadingSlash),
  map(makeProtectedRoutes),
  map(evolve({ element: (element) => <WithFunnelGuard element={element} /> })),
) as unknown) as (routes: Route[]) => RouteObject[]

// @ts-expect-error solve after removing ramda
const publicRoutes = pipe(filter(prop('public')), pluck('path'))(routes)

// @ts-expect-error solve after removing ramda
export const simpleHeaderRoutes = [...publicRoutes, paths.signup]
